import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';

// Define the backend URL (adjust if needed, e.g., use environment variables)
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5005';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

interface SocketProviderProps {
  children: ReactNode;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
});

export const useSocket = (): SocketContextType => {
  return useContext(SocketContext);
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  // Function to get the auth token (assuming it's stored in localStorage)
  // TODO: Integrate with actual auth state management if different
  const getAuthToken = useCallback((): string | null => {
    return localStorage.getItem('token'); // Adjust key if necessary
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    let newSocket: Socket | null = null;

    if (token) {
      console.log('SocketContext: Attempting to connect with token...');
      newSocket = io(SOCKET_URL, {
        query: { token }, // Pass token for authentication
        reconnectionAttempts: 5, // Optional: Limit reconnection attempts
        reconnectionDelay: 1000, // Optional: Delay between attempts
      });

      newSocket.on('connect', () => {
        console.log('SocketContext: Connected successfully!', newSocket?.id);
        setIsConnected(true);
      });

      newSocket.on('disconnect', (reason: string) => {
        console.log('SocketContext: Disconnected.', reason);
        setIsConnected(false);
        // Handle potential cleanup or reconnection logic if needed
      });

      newSocket.on('connect_error', (error: Error) => {
        console.error('SocketContext: Connection Error:', error.message);
        setIsConnected(false);
        // Handle error, maybe clear token if invalid?
      });

      setSocket(newSocket);

    } else {
      console.log('SocketContext: No auth token found, skipping connection.');
    }

    // Cleanup function
    return () => {
      if (newSocket) {
        console.log('SocketContext: Disconnecting socket...');
        newSocket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
    };
  }, [getAuthToken]); // Re-run if the way we get the token changes (though unlikely for localStorage)

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};